<template>
  <Teleport to="body">
    <Transition name="alert-fade">
      <div class="custom-alert-overlay" @click="handleOverlayClick">
        <div class="custom-alert-box" @click.stop>
          <div class="alert-icon" v-if="type">
            <span v-if="type === 'success'">✅</span>
            <span v-else-if="type === 'error'">❌</span>
            <span v-else-if="type === 'warning'">⚠️</span>
            <span v-else>ℹ️</span>
          </div>
          <div class="alert-message">{{ message }}</div>
          <button class="alert-confirm-btn" @click="handleConfirm">
            {{ confirmText }}
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
const props = defineProps({
  message: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'info', // 'success', 'error', 'warning', 'info'
    validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
  },
  confirmText: {
    type: String,
    default: '확인'
  },
  closeOnOverlay: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['confirm', 'close'])

const handleConfirm = () => {
  emit('confirm')
}

const handleOverlayClick = () => {
  if (props.closeOnOverlay) {
    emit('close')
  }
}
</script>

<style scoped>
.custom-alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.custom-alert-box {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: alertSlideIn 0.3s ease-out;
  text-align: center;
}

.alert-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  animation: iconBounce 0.5s ease-out;
}

.alert-message {
  color: white;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-weight: 500;
  word-break: keep-all;
}

.alert-confirm-btn {
  background: white;
  color: #667eea;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  min-width: 100px;
}

.alert-confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  background: #f8f9fa;
}

.alert-confirm-btn:active {
  transform: translateY(0);
}

/* 애니메이션 */
.alert-fade-enter-active,
.alert-fade-leave-active {
  transition: opacity 0.3s ease;
}

.alert-fade-enter-from,
.alert-fade-leave-to {
  opacity: 0;
}

@keyframes alertSlideIn {
  from {
    transform: translateY(-50px) scale(0.9);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

@keyframes iconBounce {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* 모바일 최적화 */
@media (max-width: 480px) {
  .custom-alert-box {
    padding: 1.5rem;
    max-width: 320px;
  }

  .alert-icon {
    font-size: 2.5rem;
  }

  .alert-message {
    font-size: 1rem;
    margin-bottom: 1.2rem;
  }

  .alert-confirm-btn {
    padding: 0.7rem 1.5rem;
    font-size: 0.95rem;
  }
}
</style>
